import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Server, Plus, Trash2, RefreshCw, CheckCircle2, XCircle, Loader2,
  Wifi, HardDrive, Cpu, Globe, Play, RotateCcw, AlertTriangle, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { formatDate, statusBadgeClass } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ServersPage() {
  const [servers, setServers] = useState([]);
  const [sites, setSites] = useState([]);
  const [deployLogs, setDeployLogs] = useState([]);
  const [health, setHealth] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [testing, setTesting] = useState({});
  const [deploying, setDeploying] = useState({});
  const [selectedLog, setSelectedLog] = useState(null);
  const [newServer, setNewServer] = useState({
    name: '', host: '', port: '22', username: 'root', auth_type: 'password',
    password: '', private_key: '', deploy_root: '/var/www',
    nginx_config_path: '/etc/nginx/sites-available',
    nginx_enabled_path: '/etc/nginx/sites-enabled', ssl_mode: 'certbot'
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [srvRes, sitesRes, logsRes] = await Promise.all([
        api.get('/servers'),
        api.get('/sites'),
        api.get('/deploy-logs'),
      ]);
      setServers(srvRes.data);
      setSites(sitesRes.data);
      setDeployLogs(logsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddServer = async () => {
    try {
      await api.post('/servers', newServer);
      toast.success('Sunucu eklendi');
      setShowAdd(false);
      setNewServer({ name: '', host: '', port: '22', username: 'root', auth_type: 'password', password: '', private_key: '', deploy_root: '/var/www', nginx_config_path: '/etc/nginx/sites-available', nginx_enabled_path: '/etc/nginx/sites-enabled', ssl_mode: 'certbot' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eklenemedi'); }
  };

  const handleTest = async (serverId) => {
    setTesting(p => ({...p, [serverId]: true}));
    try {
      const { data } = await api.post(`/servers/${serverId}/test`);
      toast[data.success ? 'success' : 'error'](data.message);
    } catch (err) { toast.error('Test basarisiz'); }
    finally { setTesting(p => ({...p, [serverId]: false})); }
  };

  const handleHealth = async (serverId) => {
    try {
      const { data } = await api.get(`/servers/${serverId}/health`);
      setHealth(p => ({...p, [serverId]: data}));
    } catch (err) { toast.error('Saglik kontrolu basarisiz'); }
  };

  const handleDeploy = async (serverId, siteId) => {
    const key = `${serverId}-${siteId}`;
    setDeploying(p => ({...p, [key]: true}));
    try {
      const { data } = await api.post(`/servers/${serverId}/deploy/${siteId}`);
      toast.success(data.message);
      setTimeout(loadData, 3000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Deploy basarisiz'); }
    finally { setDeploying(p => ({...p, [key]: false})); }
  };

  const handleDelete = async (serverId) => {
    if (!window.confirm('Bu sunucuyu silmek istediginize emin misiniz?')) return;
    try { await api.delete(`/servers/${serverId}`); toast.success('Sunucu silindi'); loadData(); }
    catch (err) { toast.error('Silinemedi'); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 rounded-xl shimmer" />)}</div>;

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Sunucu Yonetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">Remote deploy & sunucu baglantilari</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] gap-2 press-active">
              <Plus className="w-4 h-4" /> Sunucu Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg z-[100]">
            <DialogHeader><DialogTitle>Yeni Sunucu Baglantisi</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Sunucu Adi</Label><Input value={newServer.name} onChange={(e) => setNewServer({...newServer, name: e.target.value})} placeholder="Production Server" className="mt-1 bg-secondary/40" /></div>
                <div><Label className="text-xs text-muted-foreground">Host / IP</Label><Input value={newServer.host} onChange={(e) => setNewServer({...newServer, host: e.target.value})} placeholder="192.168.1.100" className="mt-1 bg-secondary/40" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-muted-foreground">Port</Label><Input value={newServer.port} onChange={(e) => setNewServer({...newServer, port: e.target.value})} className="mt-1 bg-secondary/40" /></div>
                <div><Label className="text-xs text-muted-foreground">Kullanici</Label><Input value={newServer.username} onChange={(e) => setNewServer({...newServer, username: e.target.value})} className="mt-1 bg-secondary/40" /></div>
                <div><Label className="text-xs text-muted-foreground">Auth</Label>
                  <Select value={newServer.auth_type} onValueChange={(v) => setNewServer({...newServer, auth_type: v})}>
                    <SelectTrigger className="mt-1 bg-secondary/40"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="password">Sifre</SelectItem><SelectItem value="key">SSH Key</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              {newServer.auth_type === 'password' ? (
                <div><Label className="text-xs text-muted-foreground">Sifre</Label><Input type="password" value={newServer.password} onChange={(e) => setNewServer({...newServer, password: e.target.value})} className="mt-1 bg-secondary/40" /></div>
              ) : (
                <div><Label className="text-xs text-muted-foreground">Private Key (PEM)</Label><Textarea value={newServer.private_key} onChange={(e) => setNewServer({...newServer, private_key: e.target.value})} className="mt-1 bg-secondary/40 font-mono text-xs" rows={4} placeholder="-----BEGIN RSA PRIVATE KEY-----" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Deploy Root</Label><Input value={newServer.deploy_root} onChange={(e) => setNewServer({...newServer, deploy_root: e.target.value})} className="mt-1 bg-secondary/40 font-mono text-xs" /></div>
                <div><Label className="text-xs text-muted-foreground">SSL Mode</Label>
                  <Select value={newServer.ssl_mode} onValueChange={(v) => setNewServer({...newServer, ssl_mode: v})}>
                    <SelectTrigger className="mt-1 bg-secondary/40"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="certbot">Certbot</SelectItem><SelectItem value="manual">Manuel</SelectItem><SelectItem value="disabled">Devre Disi</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleAddServer} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">Sunucuyu Kaydet</Button>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Server Cards */}
      {servers.length === 0 ? (
        <Card className="border-border/70"><CardContent className="py-12 text-center">
          <Server className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Henuz sunucu eklenmemis. Ilk sunucunuzu ekleyin.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {servers.map(server => {
            const h = health[server.id];
            return (
              <Card key={server.id} className="border-border/70">
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary/60 flex items-center justify-center"><Server className="w-5 h-5 text-muted-foreground" /></div>
                      <div>
                        <h3 className="font-semibold">{server.name || 'Sunucu'}</h3>
                        <p className="text-xs font-mono text-muted-foreground">{server.host}:{server.port} ({server.username})</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="text-xs border-border/70" onClick={() => handleTest(server.id)} disabled={testing[server.id]}>
                        {testing[server.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wifi className="w-3 h-3" />} Test
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs border-border/70" onClick={() => handleHealth(server.id)}>
                        <RefreshCw className="w-3 h-3" /> Saglik
                      </Button>
                      <Button size="sm" variant="ghost" className="text-xs text-red-400" onClick={() => handleDelete(server.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Health Metrics */}
                  {h && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                      <div className="text-center p-2 bg-secondary/30 rounded-lg">
                        <HardDrive className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">Disk</p>
                        <p className="text-sm font-semibold">{h.disk_usage || '-'}</p>
                      </div>
                      <div className="text-center p-2 bg-secondary/30 rounded-lg">
                        <Cpu className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">CPU</p>
                        <p className="text-sm font-semibold">{h.cpu_load || '-'}</p>
                      </div>
                      <div className="text-center p-2 bg-secondary/30 rounded-lg">
                        <Server className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">RAM</p>
                        <p className="text-sm font-semibold">{h.memory_usage || '-'}</p>
                      </div>
                      <div className="text-center p-2 bg-secondary/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Nginx</p>
                        <p className={`text-sm font-semibold ${h.nginx_status === 'active' ? 'text-emerald-400' : 'text-amber-400'}`}>{h.nginx_status || '-'}</p>
                      </div>
                      <div className="text-center p-2 bg-secondary/30 rounded-lg">
                        <Globe className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">Domain</p>
                        <p className="text-sm font-semibold">{h.domain_count || 0}</p>
                      </div>
                    </div>
                  )}

                  {/* Deploy Sites */}
                  <div className="border-t border-border/50 pt-3">
                    <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider font-semibold">Siteleri Deploy Et</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {sites.map(site => {
                        const key = `${server.id}-${site.id}`;
                        const lastDeploy = deployLogs.find(l => l.server_id === server.id && l.site_id === site.id);
                        return (
                          <div key={site.id} className="flex items-center justify-between p-2 rounded-lg bg-secondary/20">
                            <div>
                              <p className="text-sm font-medium">{site.brand_name}</p>
                              <p className="text-[10px] font-mono text-muted-foreground">{site.domain}</p>
                              {lastDeploy && <p className="text-[10px] text-muted-foreground">Son: {lastDeploy.status === 'success' ? '✓' : '✗'} {formatDate(lastDeploy.started_at)}</p>}
                            </div>
                            <Button size="sm" onClick={() => handleDeploy(server.id, site.id)} disabled={deploying[key]}
                              className="text-xs bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                              {deploying[key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Deploy
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Deploy Logs */}
      {deployLogs.length > 0 && (
        <Card className="border-border/70">
          <CardHeader><CardTitle className="text-base">Son Deploy Islemleri</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {deployLogs.slice(0, 15).map(log => (
                <div key={log.id} className="p-3 rounded-lg hover:bg-secondary/20 cursor-pointer" onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {log.status === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> :
                       log.status === 'running' ? <Loader2 className="w-4 h-4 text-amber-400 animate-spin" /> :
                       log.status === 'failed' ? <XCircle className="w-4 h-4 text-red-400" /> :
                       <AlertTriangle className="w-4 h-4 text-muted-foreground" />}
                      <div>
                        <p className="text-sm font-medium">{log.domain}</p>
                        <p className="text-[10px] text-muted-foreground">{formatDate(log.started_at)} - {log.pages_count || 0} sayfa</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusBadgeClass(log.status)}`}>
                        {log.status === 'success' ? 'Basarili' : log.status === 'failed' ? 'Basarisiz' : log.status === 'running' ? 'Devam Ediyor' : log.status}
                      </span>
                      <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                  </div>
                  {/* Error message always visible for failed */}
                  {log.status === 'failed' && log.message && (
                    <div className="mt-2 p-2 bg-red-400/5 border border-red-400/20 rounded-lg">
                      <p className="text-xs text-red-400 font-mono">{log.message}</p>
                    </div>
                  )}
                  {/* Expanded detail */}
                  {selectedLog?.id === log.id && (
                    <div className="mt-3 p-3 bg-secondary/30 rounded-lg border border-border/50 space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Domain:</span> <span className="font-mono">{log.domain}</span></div>
                        <div><span className="text-muted-foreground">Durum:</span> <span className={log.status === 'failed' ? 'text-red-400' : 'text-emerald-400'}>{log.status}</span></div>
                        <div><span className="text-muted-foreground">Baslangic:</span> {formatDate(log.started_at)}</div>
                        <div><span className="text-muted-foreground">Bitis:</span> {formatDate(log.completed_at)}</div>
                        <div><span className="text-muted-foreground">Sayfa:</span> {log.pages_count || 0}</div>
                        <div><span className="text-muted-foreground">Deploy:</span> {log.pages_deployed || 0} yuklendi</div>
                      </div>
                      {log.message && (
                        <div className="pt-2 border-t border-border/50">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Mesaj / Hata</p>
                          <p className="text-xs font-mono text-red-400 bg-red-400/5 p-2 rounded">{log.message}</p>
                        </div>
                      )}
                      {log.steps && log.steps.length > 0 && (
                        <div className="pt-2 border-t border-border/50">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Deploy Adimlari</p>
                          <div className="space-y-1">
                            {log.steps.map((step, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-secondary/20">
                                <div className="flex items-center gap-2">
                                  {step.status === 'ok' ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> :
                                   step.status === 'error' ? <XCircle className="w-3 h-3 text-red-400" /> :
                                   step.status === 'warning' ? <AlertTriangle className="w-3 h-3 text-amber-400" /> :
                                   step.status === 'info' ? <Eye className="w-3 h-3 text-blue-400" /> :
                                   <Loader2 className="w-3 h-3 text-muted-foreground" />}
                                  <span>{step.step}</span>
                                </div>
                                <span className={`text-[10px] ${step.status === 'ok' ? 'text-emerald-400' : step.status === 'error' ? 'text-red-400' : 'text-muted-foreground'}`}>
                                  {step.status}
                                </span>
                              </div>
                            ))}
                          </div>
                          {log.steps.some(s => s.detail) && (
                            <div className="mt-2">
                              {log.steps.filter(s => s.detail).map((s, i) => (
                                <p key={i} className="text-[10px] font-mono text-muted-foreground mt-1">[{s.step}] {s.detail}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
